import type { WorldPoint } from './types';

interface SpatialItem {
  id: number;
  position: WorldPoint;
  radius: number;
}

export class SpatialIndex<T extends SpatialItem> {
  readonly maxRadius: number;
  private readonly cells = new Map<string, Map<number, T>>();

  constructor(items: readonly T[], private readonly cellSize: number) {
    if (!(cellSize > 0)) throw new Error('Spatial index cellSize must be positive');
    this.maxRadius = items.reduce((maximum, item) => Math.max(maximum, item.radius), 0);
    for (const item of items) this.add(item);
  }

  query(point: WorldPoint, radius: number): T[] {
    const fromX = Math.floor((point.x - radius) / this.cellSize);
    const toX = Math.floor((point.x + radius) / this.cellSize);
    const fromY = Math.floor((point.y - radius) / this.cellSize);
    const toY = Math.floor((point.y + radius) / this.cellSize);
    const result = new Map<number, T>();
    for (let x = fromX; x <= toX; x += 1) for (let y = fromY; y <= toY; y += 1) {
      for (const [id, item] of this.cells.get(`${x}:${y}`) ?? []) result.set(id, item);
    }
    return [...result.values()].sort((left, right) => left.id - right.id);
  }

  update(item: T, previousPosition: WorldPoint): void {
    const previousKey = this.key(previousPosition);
    const nextKey = this.key(item.position);
    if (previousKey === nextKey) return;
    const previousCell = this.cells.get(previousKey);
    previousCell?.delete(item.id);
    if (previousCell?.size === 0) this.cells.delete(previousKey);
    this.add(item);
  }

  private add(item: T): void {
    const key = this.key(item.position);
    const cell = this.cells.get(key) ?? new Map<number, T>();
    cell.set(item.id, item);
    this.cells.set(key, cell);
  }

  private key(point: WorldPoint): string {
    return `${Math.floor(point.x / this.cellSize)}:${Math.floor(point.y / this.cellSize)}`;
  }
}
